import { motion } from "framer-motion";
import { Rocket, Network, Landmark, ShieldCheck, Scale, Globe2, GraduationCap, ArrowUpRight, ArrowRight } from "lucide-react";
import { SectionHead, Reveal, engageService, scrollToId } from "@/components/Shared";

const SERVICES = [
  {
    id: "incubation",
    icon: Rocket,
    title: "Incubation Support",
    desc: "End-to-end accelerator frameworks that carry founders from mind to market: validation, venture design, ecosystem access and capacity building alongside national innovation missions.",
    img: "https://images.pexels.com/photos/7414220/pexels-photo-7414220.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Startup founders and engineering team collaborating in an incubator workspace",
  },
  {
    id: "corporate-innovation",
    icon: Network,
    title: "Corporate Innovation Programs",
    desc: "Enterprise-to-startup matching, venture studios and structured pilots that convert emerging technology into boardroom-grade outcomes.",
    img: "https://images.unsplash.com/photo-1764810815228-b7f9432eec5c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBpbm5vdmF0&w=940&q=80",
    alt: "Modern high-tech corporate innovation lab and boardroom",
  },
  {
    id: "investor-network",
    icon: Landmark,
    title: "Investor Network",
    desc: "Curated deal-flow across venture capital funds, institutional syndicates and international banks, with preparation that survives diligence.",
    img: "https://images.pexels.com/photos/11683867/pexels-photo-11683867.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Financial district skyline at dusk representing venture capital networks",
  },
  {
    id: "ip-advisory",
    icon: ShieldCheck,
    title: "Patent & IP Advisory",
    desc: "Sovereign IP protection and commercialization strategy for deep-tech assets, from filing posture to licensing and valuation.",
    img: "https://images.pexels.com/photos/7841818/pexels-photo-7841818.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Close-up of a patent agreement document on an executive desk",
  },
  {
    id: "b2g-strategy",
    icon: Scale,
    title: "B2G Strategy",
    desc: "Government relations and policy architecture shaped by insider experience at MeitY: procurement pathways, mission alignment and regulatory navigation.",
    img: "https://images.pexels.com/photos/1398430/pexels-photo-1398430.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Neoclassical government building architecture at twilight",
  },
  {
    id: "ai-dpdp-skilling",
    icon: GraduationCap,
    title: "AI & DPDP Skilling",
    desc: "Workforce and leadership programs in applied artificial intelligence and India's DPDP Act, building data-protection fluency and AI capability for institutions and enterprises.",
    img: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHx0ZWNoJTIwd29ya3Nob3AlMjBB&w=940&q=80",
    alt: "Professional AI and data governance workshop training session",
  },
  {
    id: "global-entry",
    icon: Globe2,
    title: "Global Market Entry",
    desc: "Cross-border company setup and expansion corridors: global firms establishing in India, and Indian ventures scaling to the UK, UAE, Singapore and the US.",
    img: "https://images.pexels.com/photos/36681344/pexels-photo-36681344.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Airport departure board displaying international destinations",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 md:py-32">
      <SectionHead
        overline="Core Services"
        title={<>Counsel that moves ventures <em className="gold-text not-italic">forward.</em></>}
        testId="services-heading"
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={(i % 4) * 0.06} className="h-full">
            <motion.article
              data-testid={`service-card-${s.id}`}
              onClick={() => engageService(s.title)}
              onKeyDown={(e) => e.key === "Enter" && engageService(s.title)}
              role="button"
              tabIndex={0}
              aria-label={`Engage BYOT for ${s.title}`}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="group flex h-full cursor-pointer flex-col border border-[rgba(212,175,55,0.18)] bg-[#0F1A2E]/80 backdrop-blur-sm transition-colors duration-500 hover:border-[#D4AF37]/60 hover:bg-[#16243E]"
            >
              <div className="relative h-36 overflow-hidden">
                <img
                  src={s.img}
                  alt={s.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A2E] via-[#070E1B]/25 to-transparent" />
                <span className="absolute bottom-3 left-4 flex h-10 w-10 items-center justify-center border border-[#D4AF37]/50 bg-[#070E1B]/80 text-[#D4AF37] backdrop-blur-sm">
                  <s.icon size={18} strokeWidth={1.6} />
                </span>
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="font-serif text-xl text-[#F7F5F0] transition-colors duration-300 group-hover:text-[#E5C158]">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#94A3B8]">{s.desc}</p>
                <span className="mt-auto flex items-center gap-2 pt-6 font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]/0 transition-colors duration-500 group-hover:text-[#D4AF37]">
                  Engage <ArrowUpRight size={13} />
                </span>
              </div>
            </motion.article>
          </Reveal>
        ))}

        <Reveal delay={0.24} className="h-full">
          <button
            data-testid="services-cta-tile"
            onClick={() => scrollToId("#contact")}
            className="group flex h-full w-full flex-col justify-between border border-[#D4AF37]/70 bg-[#D4AF37]/10 p-7 text-left transition-colors duration-500 hover:bg-[#D4AF37]"
          >
            <span className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] transition-colors duration-500 group-hover:text-[#070E1B]">
              Not sure where to begin?
            </span>
            <span className="mt-6 font-serif text-xl leading-snug text-[#F7F5F0] transition-colors duration-500 group-hover:text-[#070E1B] sm:text-2xl">
              Bring us the mandate. We will shape the route.
            </span>
            <span className="mt-7 flex items-center gap-2 font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] transition-colors duration-500 group-hover:text-[#070E1B]">
              Request Consultation
              <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </button>
        </Reveal>
      </div>
    </section>
  );
}
